import * as THREE from "three";
import { RoundedBoxGeometry } from "three/addons/geometries/RoundedBoxGeometry.js";
import {
  calculateSuntraceMotionLoad,
  stepSuntraceSpring,
  type SuntraceMotionLoadProfile,
  type SuntraceMotionSnapshot,
  type SuntraceSpringState,
} from "./motion";
import type { TrackFrame } from "./track";
import type { SuntraceQuality } from "./types";
import type { SuntraceAuthoredVehicleAsset } from "./vehicleAssets";

type VehiclePalette = {
  paint: number;
  accent: number;
  solar: number;
  metal: number;
};

type VehicleGraphic =
  | "aster-arc"
  | "split-spear"
  | "coast-orbit"
  | "terrace-step"
  | "apex-wedge"
  | "pulse-bars";

type VehicleNumberTreatment =
  | "disc"
  | "shield"
  | "roundel"
  | "capsule"
  | "diamond"
  | "split";

type VehicleLiveryTone = "accent" | "solar" | "dark";

export type VehicleLiveryIdentity = {
  number: number;
  numberLabel: string;
  graphic: VehicleGraphic;
  numberTreatment: VehicleNumberTreatment;
  numberX: number;
  roof: {
    x: number;
    z: number;
    scaleX: number;
    scaleZ: number;
    yaw: number;
    tone: Exclude<VehicleLiveryTone, "dark">;
  };
  rear: {
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    rotationZ: number;
    tone: Exclude<VehicleLiveryTone, "dark">;
    mirrored: boolean;
  };
  rearLamp: {
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    rotationZ: number;
  };
  wingTone: VehicleLiveryTone;
};

const VEHICLE_LIVERY_IDENTITIES = [
  {
    number: 1,
    numberLabel: "01",
    graphic: "aster-arc",
    numberTreatment: "disc",
    numberX: 132,
    roof: { x: 0, z: -0.24, scaleX: 1.42, scaleZ: 0.92, yaw: 0, tone: "solar" },
    rear: {
      x: 0,
      y: 0.96,
      scaleX: 0.64,
      scaleY: 3.2,
      rotationZ: 0,
      tone: "solar",
      mirrored: false,
    },
    rearLamp: { x: 0.55, y: 0.79, scaleX: 0.96, scaleY: 0.88, rotationZ: 0.08 },
    wingTone: "dark",
  },
  {
    number: 2,
    numberLabel: "02",
    graphic: "split-spear",
    numberTreatment: "shield",
    numberX: 128,
    roof: { x: 0.2, z: -0.24, scaleX: 0.58, scaleZ: 0.96, yaw: 0, tone: "solar" },
    rear: {
      x: 0.47,
      y: 0.94,
      scaleX: 0.25,
      scaleY: 3.1,
      rotationZ: 0,
      tone: "solar",
      mirrored: true,
    },
    rearLamp: { x: 0.58, y: 0.78, scaleX: 0.9, scaleY: 0.82, rotationZ: 0.02 },
    wingTone: "accent",
  },
  {
    number: 3,
    numberLabel: "03",
    graphic: "coast-orbit",
    numberTreatment: "roundel",
    numberX: 134,
    roof: {
      x: 0,
      z: -0.06,
      scaleX: 1.12,
      scaleZ: 0.38,
      yaw: Math.PI / 2,
      tone: "accent",
    },
    rear: {
      x: 0.69,
      y: 0.71,
      scaleX: 0.24,
      scaleY: 3.2,
      rotationZ: Math.PI / 2,
      tone: "solar",
      mirrored: false,
    },
    rearLamp: { x: 0.62, y: 0.83, scaleX: 0.76, scaleY: 0.92, rotationZ: 0.2 },
    wingTone: "solar",
  },
  {
    number: 4,
    numberLabel: "04",
    graphic: "terrace-step",
    numberTreatment: "capsule",
    numberX: 126,
    roof: { x: -0.23, z: -0.25, scaleX: 1.04, scaleZ: 0.88, yaw: -0.05, tone: "accent" },
    rear: {
      x: -0.36,
      y: 0.94,
      scaleX: 0.46,
      scaleY: 3.1,
      rotationZ: 0.2,
      tone: "accent",
      mirrored: false,
    },
    rearLamp: { x: 0.51, y: 0.76, scaleX: 1.02, scaleY: 0.78, rotationZ: -0.05 },
    wingTone: "accent",
  },
  {
    number: 5,
    numberLabel: "05",
    graphic: "apex-wedge",
    numberTreatment: "diamond",
    numberX: 130,
    roof: { x: 0, z: 0.25, scaleX: 2.12, scaleZ: 0.29, yaw: 0, tone: "solar" },
    rear: {
      x: 0,
      y: 0.49,
      scaleX: 0.76,
      scaleY: 3.1,
      rotationZ: 0,
      tone: "solar",
      mirrored: false,
    },
    rearLamp: { x: 0.66, y: 0.84, scaleX: 0.7, scaleY: 0.9, rotationZ: 0.3 },
    wingTone: "solar",
  },
  {
    number: 6,
    numberLabel: "06",
    graphic: "pulse-bars",
    numberTreatment: "split",
    numberX: 130,
    roof: { x: 0.1, z: -0.22, scaleX: 0.82, scaleZ: 1.02, yaw: 0.16, tone: "accent" },
    rear: {
      x: 0.49,
      y: 0.92,
      scaleX: 0.3,
      scaleY: 3.1,
      rotationZ: -0.3,
      tone: "accent",
      mirrored: true,
    },
    rearLamp: { x: 0.54, y: 0.8, scaleX: 0.92, scaleY: 0.74, rotationZ: -0.16 },
    wingTone: "dark",
  },
] as const satisfies readonly VehicleLiveryIdentity[];

export const getVehicleLiveryIdentity = (
  number: number,
): VehicleLiveryIdentity => {
  const identity = VEHICLE_LIVERY_IDENTITIES[number - 1];
  if (!Number.isInteger(number) || identity?.number !== number) {
    throw new Error(`Unknown SUNTRACE vehicle number ${String(number)}`);
  }
  return identity;
};

type VehicleGeometryLibrary = {
  floor: THREE.BufferGeometry;
  shell: THREE.BufferGeometry;
  cabin: THREE.BufferGeometry;
  roof: THREE.BufferGeometry;
  tire: THREE.BufferGeometry;
  wheelArch: THREE.BufferGeometry;
  wheelWell: THREE.BufferGeometry;
  rim: THREE.BufferGeometry;
  rimBarrel: THREE.BufferGeometry;
  rimSpoke: THREE.BufferGeometry;
  rimHub: THREE.BufferGeometry;
  rimBlur: THREE.BufferGeometry;
  brakeDisc: THREE.BufferGeometry;
  caliper: THREE.BufferGeometry;
  lightBlade: THREE.BufferGeometry;
  lightHousing: THREE.BufferGeometry;
  lightEmitter: THREE.BufferGeometry;
  rearPanel: THREE.BufferGeometry;
  numberPanel: THREE.BufferGeometry;
  vent: THREE.BufferGeometry;
  skirt: THREE.BufferGeometry;
  mirror: THREE.BufferGeometry;
  bridge: THREE.BufferGeometry;
  bridgePost: THREE.BufferGeometry;
  wing: THREE.BufferGeometry;
  wingPost: THREE.BufferGeometry;
  wingEndplate: THREE.BufferGeometry;
  diffuser: THREE.BufferGeometry;
  diffuserFin: THREE.BufferGeometry;
  exhaust: THREE.BufferGeometry;
  splitter: THREE.BufferGeometry;
  detailBlade: THREE.BufferGeometry;
  glassPillar: THREE.BufferGeometry;
  rearHeader: THREE.BufferGeometry;
  panelSeam: THREE.BufferGeometry;
  contactShadow: THREE.BufferGeometry;
  dispose: () => void;
};

type VehicleLoftStation = {
  z: number;
  width: number;
  centerY: number;
  height: number;
};

const createVehicleLoftGeometry = (
  stations: readonly VehicleLoftStation[],
  radialSegments: number,
): THREE.BufferGeometry => {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const verticesPerStation = radialSegments + 1;

  for (let stationIndex = 0; stationIndex < stations.length; stationIndex += 1) {
    const station = stations[stationIndex];
    if (station === undefined) {
      continue;
    }
    for (let section = 0; section <= radialSegments; section += 1) {
      const angle = section / radialSegments * Math.PI * 2;
      const vertical = Math.cos(angle);
      const horizontal = Math.sin(angle);
      const shapedVertical = Math.sign(vertical) * Math.pow(Math.abs(vertical), 0.72);
      const shoulder = 0.93 - Math.abs(vertical) * 0.09;
      positions.push(
        horizontal * station.width * shoulder,
        station.centerY + shapedVertical * station.height,
        station.z,
      );
      uvs.push(section / radialSegments, stationIndex / (stations.length - 1));
    }
  }

  for (let station = 0; station < stations.length - 1; station += 1) {
    for (let section = 0; section < radialSegments; section += 1) {
      const current = station * verticesPerStation + section;
      const next = current + verticesPerStation;
      indices.push(current, next, current + 1, next, next + 1, current + 1);
    }
  }

  const rearCenterIndex = positions.length / 3;
  const rear = stations[0];
  const frontCenterIndex = rearCenterIndex + 1;
  const front = stations.at(-1);
  if (rear !== undefined && front !== undefined) {
    positions.push(0, rear.centerY, rear.z, 0, front.centerY, front.z);
    uvs.push(0.5, 0, 0.5, 1);
    const frontStart = (stations.length - 1) * verticesPerStation;
    for (let section = 0; section < radialSegments; section += 1) {
      indices.push(rearCenterIndex, section, section + 1);
      indices.push(frontCenterIndex, frontStart + section + 1, frontStart + section);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingSphere();
  return geometry;
};

const createVehicleGeometries = (): VehicleGeometryLibrary => {
  const bodyStations: readonly VehicleLoftStation[] = [
    { z: -2.3, width: 0.82, centerY: 0.65, height: 0.24 },
    { z: -2.08, width: 1.04, centerY: 0.68, height: 0.33 },
    { z: -1.58, width: 1.08, centerY: 0.7, height: 0.37 },
    { z: -0.9, width: 1.02, centerY: 0.71, height: 0.36 },
    { z: -0.1, width: 1, centerY: 0.69, height: 0.34 },
    { z: 0.72, width: 1.03, centerY: 0.67, height: 0.31 },
    { z: 1.38, width: 1, centerY: 0.64, height: 0.27 },
    { z: 1.96, width: 0.9, centerY: 0.61, height: 0.21 },
    { z: 2.27, width: 0.66, centerY: 0.59, height: 0.14 },
  ];
  const cabinStations: readonly VehicleLoftStation[] = [
    { z: -1.28, width: 0.52, centerY: 1.02, height: 0.06 },
    { z: -0.94, width: 0.68, centerY: 1.14, height: 0.16 },
    { z: -0.46, width: 0.74, centerY: 1.2, height: 0.23 },
    { z: 0.2, width: 0.71, centerY: 1.19, height: 0.22 },
    { z: 0.72, width: 0.62, centerY: 1.11, height: 0.14 },
    { z: 1.04, width: 0.43, centerY: 1.01, height: 0.05 },
  ];
  const tireProfile = [
    new THREE.Vector2(0.265, -0.14),
    new THREE.Vector2(0.34, -0.14),
    new THREE.Vector2(0.392, -0.105),
    new THREE.Vector2(0.405, -0.055),
    new THREE.Vector2(0.405, 0.055),
    new THREE.Vector2(0.392, 0.105),
    new THREE.Vector2(0.34, 0.14),
    new THREE.Vector2(0.265, 0.14),
  ];
  const tire = new THREE.LatheGeometry(tireProfile, 32);
  tire.rotateZ(Math.PI / 2);
  const wheelArch = new THREE.TorusGeometry(0.43, 0.07, 8, 22, Math.PI);
  wheelArch.rotateY(Math.PI / 2);
  const wheelWell = new THREE.CircleGeometry(0.385, 28);
  wheelWell.rotateY(Math.PI / 2);
  const rim = new THREE.TorusGeometry(0.184, 0.027, 8, 32);
  rim.rotateY(Math.PI / 2);
  const rimBarrel = new THREE.CylinderGeometry(0.195, 0.195, 0.19, 32, 1, true);
  rimBarrel.rotateZ(Math.PI / 2);
  const rimHub = new THREE.CylinderGeometry(0.068, 0.068, 0.2, 18);
  rimHub.rotateZ(Math.PI / 2);
  const rimBlur = new THREE.RingGeometry(0.082, 0.213, 32);
  rimBlur.rotateY(Math.PI / 2);
  const brakeDisc = new THREE.CylinderGeometry(0.16, 0.16, 0.055, 24, 1);
  brakeDisc.rotateZ(Math.PI / 2);
  const exhaust = new THREE.CylinderGeometry(0.075, 0.095, 0.28, 16, 1, true);
  exhaust.rotateX(Math.PI / 2);
  const contactShadow = new THREE.CircleGeometry(1, 32);
  contactShadow.rotateX(-Math.PI / 2);
  const geometries = {
    floor: new RoundedBoxGeometry(1.88, 0.2, 4.1, 4, 0.08),
    shell: createVehicleLoftGeometry(bodyStations, 20),
    cabin: createVehicleLoftGeometry(cabinStations, 20),
    roof: new RoundedBoxGeometry(0.18, 0.08, 1.7, 3, 0.035),
    tire,
    wheelArch,
    wheelWell,
    rim,
    rimBarrel,
    rimSpoke: new RoundedBoxGeometry(0.032, 0.052, 0.305, 2, 0.016),
    rimHub,
    rimBlur,
    brakeDisc,
    caliper: new RoundedBoxGeometry(0.1, 0.22, 0.1, 2, 0.035),
    lightBlade: new RoundedBoxGeometry(0.62, 0.065, 0.04, 2, 0.022),
    lightHousing: new RoundedBoxGeometry(0.67, 0.12, 0.075, 3, 0.034),
    lightEmitter: new RoundedBoxGeometry(0.48, 0.026, 0.018, 2, 0.01),
    rearPanel: new RoundedBoxGeometry(1.62, 0.24, 0.075, 3, 0.04),
    numberPanel: new THREE.PlaneGeometry(0.82, 0.56),
    vent: new RoundedBoxGeometry(0.34, 0.07, 0.56, 2, 0.025),
    skirt: new RoundedBoxGeometry(0.14, 0.16, 2.62, 2, 0.05),
    mirror: new RoundedBoxGeometry(0.28, 0.16, 0.42, 3, 0.07),
    bridge: new RoundedBoxGeometry(1.65, 0.12, 0.14, 3, 0.045),
    bridgePost: new RoundedBoxGeometry(0.1, 0.38, 0.12, 2, 0.035),
    wing: new RoundedBoxGeometry(1.58, 0.055, 0.22, 3, 0.025),
    wingPost: new RoundedBoxGeometry(0.065, 0.26, 0.08, 2, 0.022),
    wingEndplate: new RoundedBoxGeometry(0.032, 0.16, 0.24, 2, 0.014),
    diffuser: new RoundedBoxGeometry(1.44, 0.08, 0.4, 3, 0.03),
    diffuserFin: new RoundedBoxGeometry(0.032, 0.13, 0.32, 2, 0.012),
    exhaust,
    splitter: new RoundedBoxGeometry(1.84, 0.08, 0.46, 3, 0.03),
    detailBlade: new RoundedBoxGeometry(0.05, 0.28, 0.62, 2, 0.02),
    glassPillar: new RoundedBoxGeometry(0.075, 0.075, 0.72, 2, 0.024),
    rearHeader: new RoundedBoxGeometry(1.15, 0.07, 0.08, 3, 0.024),
    panelSeam: new RoundedBoxGeometry(1.46, 0.022, 0.032, 2, 0.01),
    contactShadow,
  } satisfies Omit<VehicleGeometryLibrary, "dispose">;
  return {
    ...geometries,
    dispose() {
      for (const geometry of Object.values(geometries)) {
        geometry.dispose();
      }
    },
  };
};

type VehiclePaintResponse = {
  roughness: number;
  clearcoat: number;
  clearcoatRoughness: number;
  specularIntensity: number;
  envMapIntensity: number;
};

export const SUNTRACE_VEHICLE_MATERIAL_RESPONSE = {
  heroPaint: {
    roughness: 0.35,
    clearcoat: 0.68,
    clearcoatRoughness: 0.2,
    specularIntensity: 0.52,
    envMapIntensity: 0.98,
  },
  rivalPaint: {
    roughness: 0.39,
    clearcoat: 0.58,
    clearcoatRoughness: 0.24,
    specularIntensity: 0.48,
    envMapIntensity: 0.92,
  },
  accentPaint: {
    roughness: 0.46,
    clearcoat: 0.36,
    clearcoatRoughness: 0.29,
    specularIntensity: 0.42,
    envMapIntensity: 0.78,
  },
  glass: {
    roughness: 0.17,
    transmission: 0.74,
    thickness: 0.075,
    ior: 1.48,
    attenuationDistance: 9,
    specularIntensity: 0.76,
    envMapIntensity: 1.08,
  },
  rubber: { roughness: 0.98, metalness: 0, envMapIntensity: 0.08 },
  metal: { roughness: 0.48, metalness: 0.68, envMapIntensity: 0.9 },
  darkMetal: { roughness: 0.62, metalness: 0.48, envMapIntensity: 0.58 },
  diffuser: { roughness: 0.67, metalness: 0.34, envMapIntensity: 0.45 },
  brake: { roughness: 0.62, metalness: 0.46, envMapIntensity: 0.62 },
  livery: {
    roughness: 0.43,
    clearcoat: 0.34,
    clearcoatRoughness: 0.28,
    specularIntensity: 0.44,
    envMapIntensity: 0.76,
  },
  contactShadow: {
    opacity: 0.8,
    loadedOpacity: 0.86,
    softWeight: 0.46,
    coreWeight: 0.54,
  },
} as const;

const createPaintMaterial = (
  color: number,
  response: VehiclePaintResponse,
  surfaceVariation = 0,
): THREE.MeshPhysicalMaterial =>
  new THREE.MeshPhysicalMaterial({
    color,
    roughness: THREE.MathUtils.clamp(
      response.roughness + surfaceVariation,
      0.3,
      0.52,
    ),
    metalness: 0,
    clearcoat: response.clearcoat,
    clearcoatRoughness: THREE.MathUtils.clamp(
      response.clearcoatRoughness - surfaceVariation * 0.4,
      0.16,
      0.32,
    ),
    specularIntensity: response.specularIntensity,
    specularColor: 0xe9ece9,
    envMapIntensity: response.envMapIntensity,
  });

const createContactShadowTexture = (): THREE.DataTexture => {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const normalizedX = (x + 0.5) / size * 2 - 1;
      const normalizedY = (y + 0.5) / size * 2 - 1;
      const bodyContact = Math.exp(
        -(normalizedX * normalizedX * 2.4 + normalizedY * normalizedY * 0.72),
      );
      const wheelX = (Math.abs(normalizedX) - 0.78) / 0.18;
      const wheelY = (Math.abs(normalizedY) - 0.65) / 0.22;
      const tireContact = Math.exp(
        -(wheelX * wheelX * 1.7 + wheelY * wheelY * 2.6),
      );
      const alpha = Math.round(
        Math.min(
          1,
          bodyContact *
            SUNTRACE_VEHICLE_MATERIAL_RESPONSE.contactShadow.softWeight +
            tireContact *
              SUNTRACE_VEHICLE_MATERIAL_RESPONSE.contactShadow.coreWeight,
        ) * 255,
      );
      const offset = (y * size + x) * 4;
      data[offset] = 0;
      data[offset + 1] = 0;
      data[offset + 2] = 0;
      data[offset + 3] = alpha;
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
};

const drawVehicleGraphic = (
  context: CanvasRenderingContext2D,
  identity: VehicleLiveryIdentity,
  paint: string,
  solar: string,
): void => {
  context.save();
  context.globalAlpha = 0.94;
  switch (identity.graphic) {
    case "aster-arc":
      context.strokeStyle = solar;
      context.lineWidth = 18;
      context.beginPath();
      context.arc(-8, 126, 110, -1.48, -0.06);
      context.stroke();
      context.strokeStyle = paint;
      context.lineWidth = 10;
      context.beginPath();
      context.arc(-8, 126, 82, -1.48, -0.03);
      context.stroke();
      context.fillStyle = paint;
      context.beginPath();
      context.moveTo(22, 106);
      context.lineTo(78, 20);
      context.lineTo(104, 20);
      context.lineTo(51, 106);
      context.closePath();
      context.fill();
      break;
    case "split-spear":
      context.fillStyle = paint;
      for (const shift of [-32, 32]) {
        context.beginPath();
        context.moveTo(72 + shift, 106);
        context.lineTo(126 + shift, 20);
        context.lineTo(148 + shift, 20);
        context.lineTo(96 + shift, 106);
        context.closePath();
        context.fill();
      }
      context.fillStyle = solar;
      context.beginPath();
      context.moveTo(196, 20);
      context.lineTo(224, 20);
      context.lineTo(171, 106);
      context.lineTo(154, 106);
      context.closePath();
      context.fill();
      break;
    case "coast-orbit":
      context.strokeStyle = paint;
      context.lineWidth = 12;
      context.beginPath();
      context.ellipse(128, 63, 114, 37, -0.12, 0, Math.PI * 2);
      context.stroke();
      context.strokeStyle = solar;
      context.lineWidth = 5;
      context.beginPath();
      context.ellipse(128, 63, 92, 27, -0.12, 0, Math.PI * 2);
      context.stroke();
      context.fillStyle = solar;
      context.beginPath();
      context.arc(224, 47, 12, 0, Math.PI * 2);
      context.fill();
      break;
    case "terrace-step":
      context.fillStyle = paint;
      context.fillRect(0, 26, 61, 20);
      context.fillRect(0, 52, 88, 20);
      context.fillRect(0, 78, 116, 20);
      context.fillStyle = solar;
      context.fillRect(187, 26, 69, 13);
      context.fillRect(207, 47, 49, 13);
      context.fillRect(227, 68, 29, 13);
      break;
    case "apex-wedge":
      context.fillStyle = paint;
      context.beginPath();
      context.moveTo(256, 20);
      context.lineTo(256, 106);
      context.lineTo(92, 106);
      context.closePath();
      context.fill();
      context.fillStyle = solar;
      context.beginPath();
      context.moveTo(256, 34);
      context.lineTo(256, 61);
      context.lineTo(154, 106);
      context.lineTo(126, 106);
      context.closePath();
      context.fill();
      break;
    case "pulse-bars":
      context.fillStyle = paint;
      for (let index = 0; index < 3; index += 1) {
        const x = 24 + index * 48;
        context.beginPath();
        context.moveTo(x, 106);
        context.lineTo(x + 42, 20);
        context.lineTo(x + 61, 20);
        context.lineTo(x + 19, 106);
        context.closePath();
        context.fill();
      }
      context.fillStyle = solar;
      context.fillRect(213, 20, 14, 86);
      break;
  }
  context.restore();
};

const drawNumberTreatment = (
  context: CanvasRenderingContext2D,
  identity: VehicleLiveryIdentity,
  paint: string,
  solar: string,
  textColor: string,
  outlineColor: string,
): void => {
  const centerX = identity.numberX;
  context.save();
  context.fillStyle = paint;
  context.strokeStyle = solar;
  context.lineWidth = 5;
  context.beginPath();
  switch (identity.numberTreatment) {
    case "disc":
      context.arc(centerX, 64, 36, 0, Math.PI * 2);
      break;
    case "shield":
      context.moveTo(centerX - 38, 31);
      context.lineTo(centerX + 38, 31);
      context.lineTo(centerX + 31, 80);
      context.lineTo(centerX, 102);
      context.lineTo(centerX - 31, 80);
      context.closePath();
      break;
    case "roundel":
      context.ellipse(centerX, 64, 41, 33, -0.14, 0, Math.PI * 2);
      break;
    case "capsule":
      context.roundRect(centerX - 47, 32, 94, 64, 20);
      break;
    case "diamond":
      context.moveTo(centerX, 25);
      context.lineTo(centerX + 45, 64);
      context.lineTo(centerX, 103);
      context.lineTo(centerX - 45, 64);
      context.closePath();
      break;
    case "split":
      context.moveTo(centerX - 48, 34);
      context.lineTo(centerX + 38, 27);
      context.lineTo(centerX + 48, 94);
      context.lineTo(centerX - 38, 101);
      context.closePath();
      break;
  }
  context.fill();
  context.stroke();
  if (identity.numberTreatment === "roundel") {
    context.strokeStyle = outlineColor;
    context.lineWidth = 2;
    context.beginPath();
    context.ellipse(centerX, 64, 32, 25, -0.14, 0, Math.PI * 2);
    context.stroke();
  }
  if (identity.numberTreatment === "split") {
    context.strokeStyle = solar;
    context.lineWidth = 8;
    context.beginPath();
    context.moveTo(centerX - 3, 31);
    context.lineTo(centerX + 8, 97);
    context.stroke();
  }
  context.font = "900 61px Arial Narrow, Arial, sans-serif";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.lineWidth = 6;
  context.strokeStyle = outlineColor;
  context.strokeText(identity.numberLabel, centerX, 65);
  context.fillStyle = textColor;
  context.fillText(identity.numberLabel, centerX, 65);
  context.restore();
};

const createLiveryTexture = (
  palette: VehiclePalette,
  identity: VehicleLiveryIdentity,
): THREE.CanvasTexture => {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 128;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("SUNTRACE requires a 2D canvas context for vehicle liveries");
  }
  const paint = `#${new THREE.Color(palette.paint).getHexString()}`;
  const accent = `#${new THREE.Color(palette.accent).getHexString()}`;
  const solar = `#${new THREE.Color(palette.solar).getHexString()}`;
  const paintColor = new THREE.Color(palette.paint);
  const paintLuminance =
    paintColor.r * 0.2126 + paintColor.g * 0.7152 + paintColor.b * 0.0722;
  const textColor = paintLuminance > 0.38 ? "#111a22" : "#f6f0e3";
  const outlineColor = paintLuminance > 0.38 ? "#f6f0e3" : "#111a22";
  context.fillStyle = accent;
  context.fillRect(0, 0, 256, 128);
  context.fillStyle = paint;
  context.fillRect(0, 0, 256, 20);
  context.fillStyle = solar;
  context.fillRect(0, 106, 256, 22);
  drawVehicleGraphic(context, identity, paint, solar);
  drawNumberTreatment(
    context,
    identity,
    paint,
    solar,
    textColor,
    outlineColor,
  );
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
};

const createVehicleMaterials = (
  palette: VehiclePalette,
  identity: VehicleLiveryIdentity,
  hero: boolean,
) => {
  const surfaceVariation = hero ? 0 : (identity.number % 3 - 1) * 0.012;
  const paint = createPaintMaterial(
    palette.paint,
    hero
      ? SUNTRACE_VEHICLE_MATERIAL_RESPONSE.heroPaint
      : SUNTRACE_VEHICLE_MATERIAL_RESPONSE.rivalPaint,
    surfaceVariation,
  );
  const accent = createPaintMaterial(
    palette.accent,
    SUNTRACE_VEHICLE_MATERIAL_RESPONSE.accentPaint,
    -surfaceVariation * 0.6,
  );
  const glass = new THREE.MeshPhysicalMaterial({
    color: 0x9eafb1,
    roughness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.glass.roughness,
    metalness: 0,
    transmission: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.glass.transmission,
    thickness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.glass.thickness,
    ior: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.glass.ior,
    attenuationColor: 0xb9c6c5,
    attenuationDistance:
      SUNTRACE_VEHICLE_MATERIAL_RESPONSE.glass.attenuationDistance,
    specularIntensity:
      SUNTRACE_VEHICLE_MATERIAL_RESPONSE.glass.specularIntensity,
    envMapIntensity: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.glass.envMapIntensity,
  });
  const interior = new THREE.MeshStandardMaterial({
    color: 0x2b363a,
    roughness: 0.84,
    metalness: 0.02,
    envMapIntensity: 0.2,
  });
  const rubber = new THREE.MeshStandardMaterial({
    color: 0x1c2122,
    roughness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.rubber.roughness,
    metalness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.rubber.metalness,
    envMapIntensity:
      SUNTRACE_VEHICLE_MATERIAL_RESPONSE.rubber.envMapIntensity,
  });
  const metal = new THREE.MeshStandardMaterial({
    color: palette.metal,
    roughness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.metal.roughness,
    metalness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.metal.metalness,
    envMapIntensity: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.metal.envMapIntensity,
  });
  const darkMetal = new THREE.MeshStandardMaterial({
    color: hero ? 0x3b4548 : 0x2b3335,
    roughness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.darkMetal.roughness,
    metalness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.darkMetal.metalness,
    envMapIntensity:
      SUNTRACE_VEHICLE_MATERIAL_RESPONSE.darkMetal.envMapIntensity,
  });
  const diffuser = new THREE.MeshStandardMaterial({
    color: hero ? 0x465154 : 0x384144,
    roughness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.diffuser.roughness,
    metalness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.diffuser.metalness,
    envMapIntensity:
      SUNTRACE_VEHICLE_MATERIAL_RESPONSE.diffuser.envMapIntensity,
  });
  const brake = new THREE.MeshStandardMaterial({
    color: palette.accent,
    roughness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.brake.roughness,
    metalness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.brake.metalness,
    envMapIntensity: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.brake.envMapIntensity,
  });
  const headlight = new THREE.MeshStandardMaterial({
    color: 0xf3efe4,
    roughness: 0.2,
    metalness: 0,
    emissive: 0xfff1cf,
    emissiveIntensity: 1.35,
  });
  const taillight = new THREE.MeshPhysicalMaterial({
    color: 0x6f1712,
    roughness: 0.3,
    metalness: 0,
    clearcoat: 0.74,
    clearcoatRoughness: 0.2,
    transmission: 0.02,
    thickness: 0.04,
    emissive: 0x8f1c14,
    emissiveIntensity: 0.3,
    envMapIntensity: 0.62,
  });
  const taillightEmitter = new THREE.MeshStandardMaterial({
    color: 0xd34535,
    roughness: 0.36,
    metalness: 0,
    emissive: 0xea3f31,
    emissiveIntensity: 0.92,
  });
  const solar = new THREE.MeshStandardMaterial({
    color: palette.solar,
    roughness: 0.42,
    metalness: 0,
    emissive: palette.solar,
    emissiveIntensity: 0.025,
    transparent: true,
    opacity: 0.92,
  });
  const contactShadowTexture = createContactShadowTexture();
  const liveryTexture = createLiveryTexture(palette, identity);
  const contactShadow = new THREE.MeshBasicMaterial({
    color: 0x0b1114,
    map: contactShadowTexture,
    transparent: true,
    opacity: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.contactShadow.opacity,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  const livery = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    map: liveryTexture,
    roughness: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.livery.roughness,
    metalness: 0,
    clearcoat: SUNTRACE_VEHICLE_MATERIAL_RESPONSE.livery.clearcoat,
    clearcoatRoughness:
      SUNTRACE_VEHICLE_MATERIAL_RESPONSE.livery.clearcoatRoughness,
    specularIntensity:
      SUNTRACE_VEHICLE_MATERIAL_RESPONSE.livery.specularIntensity,
    envMapIntensity:
      SUNTRACE_VEHICLE_MATERIAL_RESPONSE.livery.envMapIntensity,
    polygonOffset: true,
    polygonOffsetFactor: -3,
    polygonOffsetUnits: -3,
  });
  const wheelBlur = new THREE.MeshBasicMaterial({
    color: palette.metal,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const materials = [
    paint,
    accent,
    glass,
    interior,
    rubber,
    metal,
    darkMetal,
    diffuser,
    brake,
    headlight,
    taillight,
    taillightEmitter,
    solar,
    contactShadow,
    livery,
    wheelBlur,
  ];
  return {
    paint,
    accent,
    glass,
    interior,
    rubber,
    metal,
    darkMetal,
    diffuser,
    brake,
    headlight,
    taillight,
    taillightEmitter,
    solar,
    contactShadow,
    livery,
    wheelBlur,
    dispose() {
      for (const material of materials) {
        material.dispose();
      }
      contactShadowTexture.dispose();
      liveryTexture.dispose();
    },
  };
};

type VehicleMaterials = ReturnType<typeof createVehicleMaterials>;

const addMesh = (
  parent: THREE.Object3D,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  position: readonly [number, number, number],
  scale: readonly [number, number, number] = [1, 1, 1],
  rotation: readonly [number, number, number] = [0, 0, 0],
  castsShadow = true,
): THREE.Mesh => {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(...position);
  mesh.scale.set(...scale);
  mesh.rotation.set(...rotation);
  mesh.castShadow = castsShadow;
  mesh.receiveShadow = castsShadow;
  parent.add(mesh);
  return mesh;
};

const resolveLiveryTone = (
  tone: VehicleLiveryTone,
  materials: VehicleMaterials,
): THREE.Material => {
  if (tone === "accent") {
    return materials.accent;
  }
  if (tone === "solar") {
    return materials.solar;
  }
  return materials.darkMetal;
};

const addRoofLiveryIdentity = (
  group: THREE.Group,
  geometries: VehicleGeometryLibrary,
  materials: VehicleMaterials,
  identity: VehicleLiveryIdentity,
): void => {
  const roof = identity.roof;
  const treatment = addMesh(
    group,
    geometries.roof,
    resolveLiveryTone(roof.tone, materials),
    [roof.x, 1.5, roof.z],
    [roof.scaleX, 0.62, roof.scaleZ],
    [0, roof.yaw, 0],
    false,
  );
  treatment.name = `${identity.numberLabel} roof treatment`;
};

const addRearLiveryIdentity = (
  group: THREE.Group,
  geometries: VehicleGeometryLibrary,
  materials: VehicleMaterials,
  identity: VehicleLiveryIdentity,
): void => {
  const rear = identity.rear;
  const positions = rear.mirrored ? [-rear.x, rear.x] : [rear.x];
  for (const x of positions) {
    const rotationZ = rear.mirrored
      ? rear.rotationZ * Math.sign(x / rear.x)
      : rear.rotationZ;
    const treatment = addMesh(
      group,
      geometries.panelSeam,
      resolveLiveryTone(rear.tone, materials),
      [x, rear.y, -2.445],
      [rear.scaleX, rear.scaleY, 1],
      [0, 0, rotationZ],
      false,
    );
    treatment.name = `${identity.numberLabel} rear treatment`;
  }
};

type WheelVisual = {
  steeringPivot: THREE.Group;
  spinningGroup: THREE.Group;
};

const createWheel = (
  geometries: VehicleGeometryLibrary,
  materials: VehicleMaterials,
  x: number,
  z: number,
): WheelVisual => {
  const steeringPivot = new THREE.Group();
  steeringPivot.position.set(x, 0.41, z);
  const spinningGroup = new THREE.Group();
  const side = Math.sign(x);
  addMesh(spinningGroup, geometries.tire, materials.rubber, [0, 0, 0]);
  addMesh(spinningGroup, geometries.rimBarrel, materials.darkMetal, [0, 0, 0]);
  addMesh(
    spinningGroup,
    geometries.rim,
    materials.metal,
    [side * 0.112, 0, 0],
    [1, 1, 1],
    [0, 0, side > 0 ? Math.PI : 0],
  );
  addMesh(
    spinningGroup,
    geometries.rimBlur,
    materials.wheelBlur,
    [side * 0.148, 0, 0],
    [1, 1, 1],
    [0, 0, 0],
    false,
  ).renderOrder = 2;
  addMesh(
    spinningGroup,
    geometries.brakeDisc,
    materials.darkMetal,
    [side * 0.075, 0, 0],
  );
  addMesh(spinningGroup, geometries.rimHub, materials.darkMetal, [0, 0, 0]);
  for (let spoke = 0; spoke < 5; spoke += 1) {
    addMesh(
      spinningGroup,
      geometries.rimSpoke,
      spoke === 0 ? materials.brake : materials.metal,
      [side * 0.116, 0, 0],
      [1, 1, 1],
      [spoke / 5 * Math.PI * 2, 0, 0],
    );
  }
  const caliper = addMesh(
    steeringPivot,
    geometries.caliper,
    materials.brake,
    [side * 0.092, 0.03, -0.12],
  );
  caliper.rotation.y = Math.PI / 2;
  steeringPivot.add(spinningGroup);
  return { steeringPivot, spinningGroup };
};

const addHeroDetails = (
  detailGroup: THREE.Group,
  geometries: VehicleGeometryLibrary,
  materials: VehicleMaterials,
): void => {
  for (const side of [-1, 1]) {
    addMesh(
      detailGroup,
      geometries.detailBlade,
      materials.darkMetal,
      [side * 0.93, 0.6, 1.45],
      [0.8, 0.55, 0.8],
      [0, side * 0.12, side * 0.16],
    );
    addMesh(
      detailGroup,
      geometries.detailBlade,
      materials.darkMetal,
      [side * 0.92, 0.65, -1.52],
      [0.75, 0.52, 0.82],
      [0, side * -0.12, side * 0.1],
    );
  }
};

const addVariantAero = (
  group: THREE.Group,
  variant: number,
  geometries: VehicleGeometryLibrary,
  materials: VehicleMaterials,
  identity: VehicleLiveryIdentity,
): void => {
  const wingMaterial = resolveLiveryTone(identity.wingTone, materials);
  if (variant === 0 || variant === 3) {
    const wideWing = variant === 3;
    addMesh(
      group,
      geometries.wing,
      wingMaterial,
      [0, wideWing ? 1.09 : 1.12, -2.11],
      [wideWing ? 1.02 : 0.9, wideWing ? 0.56 : 0.64, wideWing ? 0.58 : 0.74],
      [-0.04, 0, 0],
    );
    for (const side of [-1, 1]) {
      addMesh(
        group,
        geometries.wingPost,
        materials.darkMetal,
        [side * (wideWing ? 0.6 : 0.52), wideWing ? 0.97 : 0.99, -2.05],
        [1, wideWing ? 0.7 : 0.76, 1],
      );
      addMesh(
        group,
        geometries.wingEndplate,
        materials.darkMetal,
        [side * (wideWing ? 0.8 : 0.7), wideWing ? 1.09 : 1.12, -2.11],
        [1, wideWing ? 0.9 : 0.82, wideWing ? 0.66 : 0.78],
        [-0.04, 0, 0],
      );
    }
  }

  if (variant === 1) {
    addMesh(
      group,
      geometries.vent,
      materials.accent,
      [0, 1.64, -0.3],
      [0.82, 0.95, 1.2],
    );
  }

  if (variant === 2) {
    for (const side of [-1, 1]) {
      addMesh(
        group,
        geometries.detailBlade,
        materials.accent,
        [side * 0.98, 0.66, 1.72],
        [1, 0.7, 0.8],
        [0, 0, side * 0.32],
      );
    }
  }

  if (variant === 4) {
    addMesh(
      group,
      geometries.wing,
      wingMaterial,
      [0, 0.96, -2.14],
      [0.72, 0.52, 0.62],
      [-0.025, 0, 0],
    );
  }
};

export type VehiclePresentation = {
  frame: TrackFrame;
  speed: number;
  heading: number;
  steering: number;
  slip: number;
  drift: number;
  boost: number;
  impact: number;
  impactSide: -1 | 0 | 1;
  time: number;
  deltaSeconds: number;
  reducedMotion?: boolean;
};

export type SuntraceSuspensionLoadInput = {
  speed: number;
  lateralLoad: number;
  verticalLoad: number;
  boostLoad: number;
  motionScale?: number;
};

export type SuntraceSuspensionTargets = {
  heaveMeters: number;
  pitchRadians: number;
  rollRadians: number;
  leftTravel: number;
  rightTravel: number;
};

const SUNTRACE_SUSPENSION_BOUNDS = {
  heaveMeters: 0.045,
  pitchRadians: 0.018,
  rollRadians: 0.032,
  wheelTravel: 0.035,
} as const;

export const calculateSuntraceSuspensionTargets = (
  input: SuntraceSuspensionLoadInput,
  target: SuntraceSuspensionTargets = {
    heaveMeters: 0,
    pitchRadians: 0,
    rollRadians: 0,
    leftTravel: 0,
    rightTravel: 0,
  },
): SuntraceSuspensionTargets => {
  const speed = Number.isFinite(input.speed)
    ? THREE.MathUtils.clamp(input.speed, 0, 110)
    : 0;
  const lateralLoad = Number.isFinite(input.lateralLoad)
    ? THREE.MathUtils.clamp(input.lateralLoad, -1, 1)
    : 0;
  const verticalLoad = Number.isFinite(input.verticalLoad)
    ? THREE.MathUtils.clamp(input.verticalLoad, -1, 1)
    : 0;
  const boostLoad = Number.isFinite(input.boostLoad)
    ? THREE.MathUtils.clamp(input.boostLoad, 0, 1)
    : 0;
  const motionScale = Number.isFinite(input.motionScale)
    ? THREE.MathUtils.clamp(input.motionScale ?? 1, 0, 1)
    : 1;
  const speedLoad = THREE.MathUtils.smoothstep(speed, 14, 78);
  const lateralTravel = -lateralLoad * speedLoad * 0.026 * motionScale;
  const verticalTravel = (verticalLoad * 0.014 + speedLoad * 0.003) * motionScale;

  target.heaveMeters = THREE.MathUtils.clamp(
    (-verticalLoad * 0.035 - boostLoad * 0.01) * motionScale,
    -SUNTRACE_SUSPENSION_BOUNDS.heaveMeters,
    SUNTRACE_SUSPENSION_BOUNDS.heaveMeters,
  );
  target.pitchRadians = THREE.MathUtils.clamp(
    (verticalLoad * 0.009 - boostLoad * 0.018) * motionScale,
    -SUNTRACE_SUSPENSION_BOUNDS.pitchRadians,
    SUNTRACE_SUSPENSION_BOUNDS.pitchRadians,
  );
  target.rollRadians = THREE.MathUtils.clamp(
    -lateralLoad * speedLoad * SUNTRACE_SUSPENSION_BOUNDS.rollRadians * motionScale,
    -SUNTRACE_SUSPENSION_BOUNDS.rollRadians,
    SUNTRACE_SUSPENSION_BOUNDS.rollRadians,
  );
  target.leftTravel = THREE.MathUtils.clamp(
    verticalTravel + lateralTravel,
    -SUNTRACE_SUSPENSION_BOUNDS.wheelTravel,
    SUNTRACE_SUSPENSION_BOUNDS.wheelTravel,
  );
  target.rightTravel = THREE.MathUtils.clamp(
    verticalTravel - lateralTravel,
    -SUNTRACE_SUSPENSION_BOUNDS.wheelTravel,
    SUNTRACE_SUSPENSION_BOUNDS.wheelTravel,
  );
  return target;
};

export type SuntraceSuspensionController = {
  update: (
    input: SuntraceSuspensionLoadInput,
    deltaSeconds: number,
  ) => Readonly<SuntraceSuspensionTargets>;
  getPose: () => Readonly<SuntraceSuspensionTargets>;
  reset: () => void;
};

const createSuspensionSpring = (): SuntraceSpringState => ({
  value: 0,
  velocity: 0,
});

const resetSuspensionSpring = (spring: SuntraceSpringState): void => {
  spring.value = 0;
  spring.velocity = 0;
};

export const createSuntraceSuspensionController = (): SuntraceSuspensionController => {
  const heaveSpring = createSuspensionSpring();
  const pitchSpring = createSuspensionSpring();
  const rollSpring = createSuspensionSpring();
  const leftTravelSpring = createSuspensionSpring();
  const rightTravelSpring = createSuspensionSpring();
  const target: SuntraceSuspensionTargets = {
    heaveMeters: 0,
    pitchRadians: 0,
    rollRadians: 0,
    leftTravel: 0,
    rightTravel: 0,
  };
  const pose: SuntraceSuspensionTargets = { ...target };

  const reset = (): void => {
    resetSuspensionSpring(heaveSpring);
    resetSuspensionSpring(pitchSpring);
    resetSuspensionSpring(rollSpring);
    resetSuspensionSpring(leftTravelSpring);
    resetSuspensionSpring(rightTravelSpring);
    pose.heaveMeters = 0;
    pose.pitchRadians = 0;
    pose.rollRadians = 0;
    pose.leftTravel = 0;
    pose.rightTravel = 0;
  };

  return {
    update(input, deltaSeconds) {
      const delta = Number.isFinite(deltaSeconds)
        ? THREE.MathUtils.clamp(deltaSeconds, 0, 0.05)
        : 0;
      if (delta === 0) {
        return pose;
      }
      calculateSuntraceSuspensionTargets(input, target);
      stepSuntraceSpring(heaveSpring, target.heaveMeters, 76, 1.05, delta);
      stepSuntraceSpring(pitchSpring, target.pitchRadians, 70, 1.05, delta);
      stepSuntraceSpring(rollSpring, target.rollRadians, 82, 1.05, delta);
      stepSuntraceSpring(leftTravelSpring, target.leftTravel, 92, 1.08, delta);
      stepSuntraceSpring(rightTravelSpring, target.rightTravel, 92, 1.08, delta);
      pose.heaveMeters = THREE.MathUtils.clamp(
        heaveSpring.value,
        -SUNTRACE_SUSPENSION_BOUNDS.heaveMeters,
        SUNTRACE_SUSPENSION_BOUNDS.heaveMeters,
      );
      pose.pitchRadians = THREE.MathUtils.clamp(
        pitchSpring.value,
        -SUNTRACE_SUSPENSION_BOUNDS.pitchRadians,
        SUNTRACE_SUSPENSION_BOUNDS.pitchRadians,
      );
      pose.rollRadians = THREE.MathUtils.clamp(
        rollSpring.value,
        -SUNTRACE_SUSPENSION_BOUNDS.rollRadians,
        SUNTRACE_SUSPENSION_BOUNDS.rollRadians,
      );
      pose.leftTravel = THREE.MathUtils.clamp(
        leftTravelSpring.value,
        -SUNTRACE_SUSPENSION_BOUNDS.wheelTravel,
        SUNTRACE_SUSPENSION_BOUNDS.wheelTravel,
      );
      pose.rightTravel = THREE.MathUtils.clamp(
        rightTravelSpring.value,
        -SUNTRACE_SUSPENSION_BOUNDS.wheelTravel,
        SUNTRACE_SUSPENSION_BOUNDS.wheelTravel,
      );
      return pose;
    },
    getPose() {
      return pose;
    },
    reset,
  };
};

export type SuntraceSuspensionWheel = {
  pivot: THREE.Object3D;
  side: "left" | "right";
};

export type SuntraceSuspensionPresentation = {
  update: (
    input: SuntraceSuspensionLoadInput,
    deltaSeconds: number,
  ) => Readonly<SuntraceSuspensionTargets>;
  getPose: () => Readonly<SuntraceSuspensionTargets>;
  reset: () => void;
  dispose: () => void;
};

export const createSuntraceSuspensionPresentation = (
  bodyPresentation: THREE.Object3D,
  wheels: readonly SuntraceSuspensionWheel[],
): SuntraceSuspensionPresentation => {
  const controller = createSuntraceSuspensionController();
  const neutralBodyY = bodyPresentation.position.y;
  const neutralBodyPitch = bodyPresentation.rotation.x;
  const neutralBodyRoll = bodyPresentation.rotation.z;
  const neutralWheelHeights = wheels.map(({ pivot }) => pivot.position.y);

  const applyPose = (pose: Readonly<SuntraceSuspensionTargets>): void => {
    bodyPresentation.position.y = neutralBodyY + pose.heaveMeters;
    bodyPresentation.rotation.x = neutralBodyPitch + pose.pitchRadians;
    bodyPresentation.rotation.z = neutralBodyRoll + pose.rollRadians;
    for (let index = 0; index < wheels.length; index += 1) {
      const wheel = wheels[index];
      if (wheel === undefined) {
        continue;
      }
      wheel.pivot.position.y = (neutralWheelHeights[index] ?? 0) +
        (wheel.side === "left" ? pose.leftTravel : pose.rightTravel);
    }
  };

  const reset = (): void => {
    controller.reset();
    applyPose(controller.getPose());
  };

  return {
    update(input, deltaSeconds) {
      const pose = controller.update(input, deltaSeconds);
      applyPose(pose);
      return pose;
    },
    getPose() {
      return controller.getPose();
    },
    reset,
    dispose: reset,
  };
};

export type VehicleMotionInput = {
  speed: number;
  steering: number;
  slip: number;
  boost: number;
  impact: number;
  time: number;
  deltaSeconds: number;
  travelDistance: number;
  phaseOffset: number;
};

export const calculateVehicleMotionProfile = (
  input: VehicleMotionInput,
) => {
  const speed = Number.isFinite(input.speed) ? Math.max(0, input.speed) : 0;
  const steering = Number.isFinite(input.steering)
    ? THREE.MathUtils.clamp(input.steering, -1, 1)
    : 0;
  const slip = Number.isFinite(input.slip)
    ? THREE.MathUtils.clamp(input.slip, -1, 1)
    : 0;
  const boost = Number.isFinite(input.boost)
    ? THREE.MathUtils.clamp(input.boost, 0, 1)
    : 0;
  const impact = Number.isFinite(input.impact)
    ? THREE.MathUtils.clamp(input.impact, 0, 1)
    : 0;
  const time = Number.isFinite(input.time) ? Math.max(0, input.time) : 0;
  const deltaSeconds = Number.isFinite(input.deltaSeconds)
    ? THREE.MathUtils.clamp(input.deltaSeconds, 0, 0.05)
    : 0;
  const phaseOffset = Number.isFinite(input.phaseOffset) ? input.phaseOffset : 0;
  const previousDistance = Number.isFinite(input.travelDistance)
    ? input.travelDistance
    : 0;
  const distanceDelta = speed * deltaSeconds;
  const travelDistance =
    (previousDistance + distanceDelta) % (50 * Math.PI);
  const speedFactor = THREE.MathUtils.clamp(speed / 82, 0, 1);
  const primaryRoad = Math.sin(travelDistance * 0.92 + phaseOffset);
  const secondaryRoad = Math.sin(travelDistance * 2.64 + phaseOffset * 1.7);
  const impactPulse = Math.sin(time * 37 + phaseOffset) * impact;
  const bodyHeave =
    (primaryRoad * 0.0036 + secondaryRoad * 0.0018) * speedFactor -
    speedFactor * speedFactor * 0.0035 +
    impact * 0.007 +
    impactPulse * 0.011;
  const bodyRoll =
    -steering * (0.028 + speedFactor * 0.038) -
    slip * 0.064 +
    impactPulse * 0.016;
  const bodyPitch =
    -boost * 0.014 +
    primaryRoad * speedFactor * 0.0028 +
    impactPulse * 0.02;

  return {
    speedFactor,
    travelDistance,
    distanceDelta,
    bodyHeave: bodyHeave || 0,
    bodyRoll: bodyRoll || 0,
    bodyPitch: bodyPitch || 0,
    frontSteer: steering * 0.48 - slip * 0.24 || 0,
    wheelAngleDelta: -distanceDelta / 0.405 || 0,
    wheelBlurOpacity: Math.pow(speedFactor, 1.8) * 0.065,
  };
};

export type VehicleChassisResponseState = {
  initialized: boolean;
  roadBank: number;
  roadGrade: number;
  roll: number;
  rollVelocity: number;
  pitch: number;
  pitchVelocity: number;
  heave: number;
  heaveVelocity: number;
  previousImpact: number;
  impactSide: -1 | 0 | 1;
};

export type VehicleChassisResponseInput = {
  roadBank: number;
  roadGrade: number;
  speed: number;
  steering: number;
  slip: number;
  impact: number;
  impactSide: -1 | 0 | 1;
  deltaSeconds: number;
  responseScale: number;
};

export const createVehicleChassisResponseState = (): VehicleChassisResponseState => ({
  initialized: false,
  roadBank: 0,
  roadGrade: 0,
  roll: 0,
  rollVelocity: 0,
  pitch: 0,
  pitchVelocity: 0,
  heave: 0,
  heaveVelocity: 0,
  previousImpact: 0,
  impactSide: 0,
});

const getAngularDelta = (current: number, previous: number): number =>
  Math.atan2(Math.sin(current - previous), Math.cos(current - previous));

export const stepVehicleChassisResponse = (
  state: VehicleChassisResponseState,
  input: VehicleChassisResponseInput,
): VehicleChassisResponseState => {
  const deltaSeconds = Number.isFinite(input.deltaSeconds)
    ? THREE.MathUtils.clamp(input.deltaSeconds, 0, 0.05)
    : 0;
  if (deltaSeconds === 0) {
    return state;
  }

  const roadBank = Number.isFinite(input.roadBank)
    ? THREE.MathUtils.clamp(input.roadBank, -Math.PI / 2, Math.PI / 2)
    : Number.isFinite(state.roadBank)
      ? state.roadBank
      : 0;
  const roadGrade = Number.isFinite(input.roadGrade)
    ? THREE.MathUtils.clamp(input.roadGrade, -Math.PI / 2, Math.PI / 2)
    : Number.isFinite(state.roadGrade)
      ? state.roadGrade
      : 0;
  const speed = Number.isFinite(input.speed) ? Math.max(0, input.speed) : 0;
  const steering = Number.isFinite(input.steering)
    ? THREE.MathUtils.clamp(input.steering, -1, 1)
    : 0;
  const slip = Number.isFinite(input.slip)
    ? THREE.MathUtils.clamp(input.slip, -1, 1)
    : 0;
  const impact = Number.isFinite(input.impact)
    ? THREE.MathUtils.clamp(input.impact, 0, 1)
    : 0;
  const impactSide = input.impactSide === -1 || input.impactSide === 1
    ? input.impactSide
    : 0;
  const responseScale = Number.isFinite(input.responseScale)
    ? THREE.MathUtils.clamp(input.responseScale, 0, 1.25)
    : 1;

  state.roll = Number.isFinite(state.roll)
    ? THREE.MathUtils.clamp(state.roll, -0.12, 0.12)
    : 0;
  state.rollVelocity = Number.isFinite(state.rollVelocity)
    ? THREE.MathUtils.clamp(state.rollVelocity, -1.8, 1.8)
    : 0;
  state.pitch = Number.isFinite(state.pitch)
    ? THREE.MathUtils.clamp(state.pitch, -0.095, 0.095)
    : 0;
  state.pitchVelocity = Number.isFinite(state.pitchVelocity)
    ? THREE.MathUtils.clamp(state.pitchVelocity, -1.5, 1.5)
    : 0;
  state.heave = Number.isFinite(state.heave)
    ? THREE.MathUtils.clamp(state.heave, -0.035, 0.028)
    : 0;
  state.heaveVelocity = Number.isFinite(state.heaveVelocity)
    ? THREE.MathUtils.clamp(state.heaveVelocity, -0.8, 0.8)
    : 0;
  state.previousImpact = Number.isFinite(state.previousImpact)
    ? THREE.MathUtils.clamp(state.previousImpact, 0, 1)
    : 0;
  state.impactSide = state.impactSide === -1 || state.impactSide === 1
    ? state.impactSide
    : 0;

  if (!state.initialized || !Number.isFinite(state.roadBank) || !Number.isFinite(state.roadGrade)) {
    state.roadBank = roadBank;
    state.roadGrade = roadGrade;
    state.initialized = true;
  } else {
    const bankDelta = THREE.MathUtils.clamp(
      getAngularDelta(roadBank, state.roadBank),
      -0.045,
      0.045,
    );
    const gradeDelta = THREE.MathUtils.clamp(
      getAngularDelta(roadGrade, state.roadGrade),
      -0.036,
      0.036,
    );
    state.roll = THREE.MathUtils.clamp(
      state.roll - bankDelta * 0.94 * responseScale,
      -0.12,
      0.12,
    );
    state.pitch = THREE.MathUtils.clamp(
      state.pitch + gradeDelta * 0.9 * responseScale,
      -0.095,
      0.095,
    );
    state.roadBank = roadBank;
    state.roadGrade = roadGrade;
  }

  const impactRise = Math.max(0, impact - state.previousImpact);
  const impactSideChanged = impactSide !== 0 && impactSide !== state.impactSide;
  if (impactSide !== 0) {
    state.impactSide = impactSide;
  }
  const impactImpulse = Math.max(
    impactRise,
    impactSideChanged && impact >= 0.2 ? impact * 0.72 : 0,
  );
  if (impactImpulse > 0) {
    state.rollVelocity -= state.impactSide * impactImpulse * 1.05 * responseScale;
    state.pitchVelocity += impactImpulse * 0.62 * responseScale;
    state.heaveVelocity -= impactImpulse * 0.34 * responseScale;
  }
  state.previousImpact = impact;

  const speedFactor = THREE.MathUtils.clamp(speed / 82, 0, 1);
  const rollTarget = THREE.MathUtils.clamp(
    (
      -steering * (0.012 + speedFactor * 0.018) -
      slip * 0.028 -
      state.impactSide * impact * 0.022
    ) * responseScale,
    -0.065,
    0.065,
  );
  const pitchTarget = impact * 0.018 * responseScale;
  const heaveTarget = -impact * 0.009 * responseScale;
  const substepCount = Math.max(1, Math.ceil(deltaSeconds * 120));
  const substepSeconds = deltaSeconds / substepCount;
  const rollDamping = Math.exp(-9.8 * substepSeconds);
  const pitchDamping = Math.exp(-10.5 * substepSeconds);
  const heaveDamping = Math.exp(-13.5 * substepSeconds);

  for (let step = 0; step < substepCount; step += 1) {
    state.rollVelocity += (rollTarget - state.roll) * 88 * substepSeconds;
    state.rollVelocity *= rollDamping;
    state.roll += state.rollVelocity * substepSeconds;
    state.pitchVelocity += (pitchTarget - state.pitch) * 100 * substepSeconds;
    state.pitchVelocity *= pitchDamping;
    state.pitch += state.pitchVelocity * substepSeconds;
    state.heaveVelocity += (heaveTarget - state.heave) * 130 * substepSeconds;
    state.heaveVelocity *= heaveDamping;
    state.heave += state.heaveVelocity * substepSeconds;
  }

  state.roll = THREE.MathUtils.clamp(state.roll, -0.12, 0.12) || 0;
  state.rollVelocity = THREE.MathUtils.clamp(state.rollVelocity, -1.8, 1.8) || 0;
  state.pitch = THREE.MathUtils.clamp(state.pitch, -0.095, 0.095) || 0;
  state.pitchVelocity = THREE.MathUtils.clamp(state.pitchVelocity, -1.5, 1.5) || 0;
  state.heave = THREE.MathUtils.clamp(state.heave, -0.035, 0.028) || 0;
  state.heaveVelocity = THREE.MathUtils.clamp(state.heaveVelocity, -0.8, 0.8) || 0;
  return state;
};

export type SuntraceVehicleVisual = {
  root: THREE.Group;
  setPresentation: (presentation: VehiclePresentation) => void;
  resetPresentation: () => void;
  setDistanceEmphasis: (emphasis: number) => void;
  setQuality: (quality: SuntraceQuality) => void;
  setVisible: (visible: boolean) => void;
  dispose: () => void;
};

export const shouldVehicleCastShadow = (
  hero: boolean,
  quality: SuntraceQuality,
): boolean => hero && quality !== "low";

const createVehicleVisual = (
  geometries: VehicleGeometryLibrary,
  palette: VehiclePalette,
  variant: number,
  hero: boolean,
  authoredAsset: SuntraceAuthoredVehicleAsset | null = null,
): SuntraceVehicleVisual => {
  const authoredHero = hero ? authoredAsset : null;
  const identity = getVehicleLiveryIdentity(hero ? 1 : variant + 2);
  const materials = createVehicleMaterials(palette, identity, hero);
  const root = new THREE.Group();
  root.name = hero
    ? "Asterion GT hero"
    : `Rival prototype ${String(variant + 1)}`;
  const motionRig = new THREE.Group();
  motionRig.name = `${root.name} motion presentation`;
  const body = new THREE.Group();
  body.name = `${root.name} body presentation`;
  const detailGroup = new THREE.Group();
  const solarGroup = new THREE.Group();
  root.add(motionRig);
  motionRig.add(body);
  body.add(detailGroup, solarGroup);
  const contactPatch = addMesh(
    motionRig,
    geometries.contactShadow,
    materials.contactShadow,
    [0, 0.008, 0],
    [1.02, 1, 2.05],
    [0, 0, 0],
    false,
  );
  contactPatch.renderOrder = 1;

  addMesh(body, geometries.floor, materials.darkMetal, [0, 0.42, -0.04]);
  addMesh(body, geometries.shell, materials.paint, [0, 0, 0]);
  addMesh(body, geometries.rearPanel, materials.darkMetal, [0, 0.67, -2.27]);
  const rearNumberPanel = addMesh(
    body,
    geometries.numberPanel,
    materials.livery,
    [0, 0.68, -2.331],
    [0.62, 0.38, 1],
    [0, Math.PI, 0],
    false,
  );
  rearNumberPanel.name = `${identity.numberLabel} rear number`;
  for (const side of [-1, 1]) {
    const rearLamp = identity.rearLamp;
    addMesh(
      body,
      geometries.skirt,
      materials.paint,
      [side * 1.02, 0.48, -0.02],
    );
    addMesh(
      body,
      geometries.mirror,
      materials.accent,
      [side * 1.08, 1.03, 0.24],
      [0.72, 0.72, 0.72],
      [0, side * 0.16, side * 0.06],
    );
    addMesh(
      body,
      geometries.vent,
      materials.darkMetal,
      [side * 0.68, 0.78, 1.48],
      [1.05, 0.68, 0.82],
      [0, 0, side * 0.08],
    );
    addMesh(
      body,
      geometries.lightBlade,
      materials.headlight,
      [side * 0.55, 0.73, 2.07],
      [1, 1, 1],
      [0, side * 0.1, side * -0.13],
      false,
    );
    addMesh(
      body,
      geometries.lightHousing,
      materials.darkMetal,
      [side * rearLamp.x, rearLamp.y, -2.345],
      [rearLamp.scaleX * 1.08, rearLamp.scaleY * 1.34, 1],
      [0, side * 0.05, side * rearLamp.rotationZ],
      false,
    );
    addMesh(
      body,
      geometries.lightBlade,
      materials.taillight,
      [side * rearLamp.x, rearLamp.y, -2.391],
      [rearLamp.scaleX, rearLamp.scaleY, 1],
      [0, side * 0.05, side * rearLamp.rotationZ],
      false,
    );
    addMesh(
      body,
      geometries.lightEmitter,
      materials.taillightEmitter,
      [side * rearLamp.x, rearLamp.y, -2.414],
      [rearLamp.scaleX * 0.82, rearLamp.scaleY * 0.76, 1],
      [0, side * 0.05, side * rearLamp.rotationZ],
      false,
    );
    addMesh(
      body,
      geometries.skirt,
      materials.paint,
      [side * 0.62, 1.01, -0.6],
      [0.88, 0.62, 0.62],
      [-0.1, 0, side * 0.04],
    );
    for (const wheelZ of [-1.36, 1.34]) {
      addMesh(
        body,
        geometries.wheelWell,
        materials.interior,
        [side * 0.982, 0.43, wheelZ],
        [1, 1, 1],
        [0, side < 0 ? Math.PI : 0, 0],
        false,
      );
      addMesh(
        body,
        geometries.wheelArch,
        materials.paint,
        [side * 0.99, 0.43, wheelZ],
      );
    }
    const sideNumberPanel = addMesh(
      body,
      geometries.numberPanel,
      materials.livery,
      [side * 1.042, 0.68, 0.08],
      [1.06, 0.62, 1],
      [0, side > 0 ? Math.PI / 2 : -Math.PI / 2, 0],
      false,
    );
    sideNumberPanel.name = `${identity.numberLabel} side number`;
    const exhaust = addMesh(
      body,
      geometries.exhaust,
      materials.darkMetal,
      [side * 0.42, 0.34, -2.43],
      [0.78, 0.78, 0.7],
    );
    exhaust.name = `${identity.numberLabel} rear sport exhaust ${String(side)}`;
  }
  addRearLiveryIdentity(body, geometries, materials, identity);
  addMesh(
    body,
    geometries.cabin,
    materials.interior,
    [0, -0.035, 0],
    [0.89, 0.84, 0.89],
    [0, 0, 0],
    false,
  );
  addMesh(
    body,
    geometries.cabin,
    materials.glass,
    [0, 0, 0],
    [1, 1, 1],
    [0, 0, 0],
    false,
  );
  for (const side of [-1, 1]) {
    addMesh(
      body,
      geometries.glassPillar,
      materials.paint,
      [side * 0.57, 1.2, -0.76],
      [1, 1, 1],
      [-0.34, 0, side * 0.04],
    );
  }
  addMesh(body, geometries.rearHeader, materials.paint, [0, 1.31, -0.93]);
  addMesh(body, geometries.panelSeam, materials.darkMetal, [0, 1.075, -1.5], [1, 1, 1], [0, 0, 0], false);
  addMesh(body, geometries.roof, materials.paint, [0, 1.45, -0.25]);
  addMesh(
    body,
    geometries.diffuser,
    materials.diffuser,
    [0, 0.31, -2.08],
    [1, 0.86, 0.92],
    [-0.08, 0, 0],
  );
  for (const x of [-0.46, 0, 0.46]) {
    addMesh(
      body,
      geometries.diffuserFin,
      materials.darkMetal,
      [x, 0.3, -2.1],
      [1, x === 0 ? 1.04 : 0.92, 0.9],
      [-0.07, 0, 0],
    );
  }
  addMesh(body, geometries.splitter, materials.darkMetal, [0, 0.4, 2.08]);
  addRoofLiveryIdentity(solarGroup, geometries, materials, identity);
  addVariantAero(body, variant, geometries, materials, identity);
  if (hero) {
    addHeroDetails(detailGroup, geometries, materials);
    addMesh(
      body,
      geometries.wing,
      resolveLiveryTone(identity.wingTone, materials),
      [0, 1, -2.14],
      [0.86, 0.58, 0.68],
      [-0.04, 0, 0],
    );
    for (const side of [-1, 1]) {
      addMesh(
        body,
        geometries.wingPost,
        materials.darkMetal,
        [side * 0.48, 0.89, -2.08],
        [1, 0.66, 1],
      );
      addMesh(
        body,
        geometries.wingEndplate,
        materials.darkMetal,
        [side * 0.67, 1, -2.14],
        [1, 0.82, 0.72],
        [-0.04, 0, 0],
      );
    }
  }

  const wheels = [
    createWheel(geometries, materials, -0.985, 1.34),
    createWheel(geometries, materials, 0.985, 1.34),
    createWheel(geometries, materials, -0.99, -1.36),
    createWheel(geometries, materials, 0.99, -1.36),
  ];
  for (let index = 0; index < wheels.length; index += 1) {
    const wheel = wheels[index];
    if (wheel === undefined) {
      continue;
    }
    wheel.steeringPivot.name = `${root.name} wheel ${String(index + 1)} suspension pivot`;
    motionRig.add(wheel.steeringPivot);
  }

  let shadowProxyMaterial: THREE.MeshBasicMaterial | null = null;
  let shadowProxyGeometry: THREE.BoxGeometry | null = null;
  let shadowProxy: THREE.Mesh | null = null;
  if (authoredHero !== null) {
    for (const child of body.children) {
      child.visible = false;
    }
    for (const wheel of wheels) {
      wheel.steeringPivot.visible = false;
    }
    body.add(authoredHero.root);
    shadowProxyMaterial = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthWrite: false,
    });
    shadowProxyGeometry = new THREE.BoxGeometry(1.88, 0.2, 4.1);
    shadowProxy = addMesh(
      body,
      shadowProxyGeometry,
      shadowProxyMaterial,
      [0, 0.42, -0.04],
      [1.02, 0.9, 1],
    );
    shadowProxy.name = "SUNTRACE hero shadow proxy";
    shadowProxy.receiveShadow = false;
  }

  const poseQuaternion = new THREE.Quaternion();
  const headingQuaternion = new THREE.Quaternion();
  const localUp = new THREE.Vector3(0, 1, 0);
  const heroScale = hero ? 1 : 0.98;
  const motionPhase = (variant + 1) * 1.37 + (hero ? 0.41 : 0);
  const suspensionWheels: SuntraceSuspensionWheel[] = authoredHero === null
    ? wheels.map((wheel) => ({
        pivot: wheel.steeringPivot,
        side: wheel.steeringPivot.position.x > 0 ? "left" : "right",
      }))
    : authoredHero.wheels.map((wheel) => ({
        pivot: wheel.steeringPivot,
        side: wheel.name.endsWith("L") ? "left" : "right",
      }));
  const suspensionPresentation = createSuntraceSuspensionPresentation(
    body,
    suspensionWheels,
  );
  const suspensionMotionSnapshot: SuntraceMotionSnapshot = {
    trackProgress: 0,
    lateralOffset: 0,
    speed: 0,
    heading: 0,
    steering: 0,
    slip: 0,
    drift: 0,
    charge: 0,
    boost: 0,
    impact: 0,
    impactSide: 0,
    rivals: [],
    phase: "racing",
    time: 0,
    acceleration: 0,
  };
  const suspensionLoadProfile: SuntraceMotionLoadProfile = {
    speedLoad: 0,
    lateralLoad: 0,
    cornerLoad: 0,
    verticalLoad: 0,
    boostLoad: 0,
    sprayLoad: 0,
    motionScale: 1,
  };
  let wheelAngle = 0;
  let travelDistance = 0;
  let previousSuspensionSpeed: number | null = null;
  let suspensionInitialized = false;
  root.scale.setScalar(heroScale);

  const resetPresentation = (): void => {
    suspensionPresentation.reset();
    previousSuspensionSpeed = null;
    suspensionInitialized = false;
  };

  return {
    root,
    setPresentation(presentation) {
      root.position.copy(presentation.frame.position);
      poseQuaternion.copy(presentation.frame.quaternion);
      const headingMagnitude = Math.abs(presentation.heading);
      const emphasizedHeading =
        Math.sign(presentation.heading) *
        Math.min(
          0.68,
          headingMagnitude * (1 + presentation.drift * 0.72) +
            Math.min(0.11, headingMagnitude * 0.9) * presentation.drift,
        );
      headingQuaternion.setFromAxisAngle(
        localUp,
        emphasizedHeading,
      );
      root.quaternion.copy(poseQuaternion).multiply(headingQuaternion);
      const motion = calculateVehicleMotionProfile({
        speed: presentation.speed,
        steering: presentation.steering,
        slip: presentation.slip,
        boost: presentation.boost,
        impact: presentation.impact,
        time: presentation.time,
        deltaSeconds: presentation.deltaSeconds,
        travelDistance,
        phaseOffset: motionPhase,
      });
      travelDistance = motion.travelDistance;
      motionRig.position.y = 0.008;
      motionRig.rotation.set(0, 0, 0);
      const suspensionDelta = Number.isFinite(presentation.deltaSeconds)
        ? THREE.MathUtils.clamp(presentation.deltaSeconds, 0, 0.05)
        : 0;
      const suspensionSpeed = Number.isFinite(presentation.speed)
        ? THREE.MathUtils.clamp(presentation.speed, 0, 110)
        : 0;
      let suspensionPose = suspensionPresentation.getPose();
      if (!suspensionInitialized) {
        suspensionPresentation.reset();
        previousSuspensionSpeed = suspensionSpeed;
        suspensionInitialized = true;
      } else if (suspensionDelta > 0) {
        suspensionMotionSnapshot.speed = suspensionSpeed;
        suspensionMotionSnapshot.heading = presentation.heading;
        suspensionMotionSnapshot.steering = presentation.steering;
        suspensionMotionSnapshot.slip = presentation.slip;
        suspensionMotionSnapshot.drift = presentation.drift;
        suspensionMotionSnapshot.boost = presentation.boost;
        suspensionMotionSnapshot.impact = presentation.impact;
        suspensionMotionSnapshot.impactSide = presentation.impactSide;
        suspensionMotionSnapshot.time = presentation.time;
        suspensionMotionSnapshot.acceleration = previousSuspensionSpeed === null
          ? 0
          : THREE.MathUtils.clamp(
              (suspensionSpeed - previousSuspensionSpeed) / suspensionDelta,
              -12,
              12,
            );
        previousSuspensionSpeed = suspensionSpeed;
        calculateSuntraceMotionLoad(
          suspensionMotionSnapshot,
          presentation.reducedMotion ?? false,
          suspensionLoadProfile,
        );
        suspensionPose = suspensionPresentation.update({
          speed: suspensionSpeed,
          lateralLoad: suspensionLoadProfile.lateralLoad,
          verticalLoad: suspensionLoadProfile.verticalLoad,
          boostLoad: suspensionLoadProfile.boostLoad,
          motionScale: suspensionLoadProfile.motionScale,
        }, suspensionDelta);
      }
      const contactLoad = THREE.MathUtils.clamp(
        motion.speedFactor * 0.18 + presentation.impact * 0.55 -
          Math.max(0, suspensionPose.heaveMeters) * 8 -
          Math.min(0, suspensionPose.heaveMeters) * 14,
        0,
        1,
      );
      materials.contactShadow.opacity = THREE.MathUtils.lerp(
        SUNTRACE_VEHICLE_MATERIAL_RESPONSE.contactShadow.opacity,
        SUNTRACE_VEHICLE_MATERIAL_RESPONSE.contactShadow.loadedOpacity,
        contactLoad,
      );
      contactPatch.scale.x = 1 + presentation.impact * 0.025;
      contactPatch.scale.z =
        2.02 + motion.speedFactor * 0.04 - presentation.impact * 0.03;
      const wheelAngleDelta = authoredHero === null
        ? motion.wheelAngleDelta
        : -motion.distanceDelta / authoredHero.wheelRadius;
      wheelAngle = (wheelAngle + wheelAngleDelta) % (Math.PI * 2);
      if (authoredHero === null) {
        for (let index = 0; index < wheels.length; index += 1) {
          const wheel = wheels[index];
          if (wheel === undefined) {
            continue;
          }
          wheel.steeringPivot.rotation.y = index < 2 ? motion.frontSteer : 0;
          wheel.spinningGroup.rotation.x = wheelAngle;
        }
      } else {
        authoredHero.setWheelPose(motion.frontSteer, wheelAngle);
      }
      const energy = THREE.MathUtils.clamp(
        presentation.drift * 0.72 + presentation.boost,
        0,
        1,
      );
      materials.solar.opacity = 0.86 + energy * 0.14;
      materials.solar.emissiveIntensity = 0.04 + energy * 0.18;
      solarGroup.scale.x = 1 + energy * 0.018;
      solarGroup.scale.y = 1 + Math.sin(presentation.time * (6 + energy * 14)) * energy * 0.025;
      materials.taillightEmitter.emissiveIntensity = 1.05 + presentation.impact * 0.38;
      materials.wheelBlur.opacity = motion.wheelBlurOpacity;
      authoredHero?.setEnergy(energy, presentation.impact);
    },
    resetPresentation,
    setQuality(quality) {
      if (authoredHero === null) {
        detailGroup.visible = hero || quality === "high";
        root.traverse((object) => {
          if (object instanceof THREE.Mesh) {
            object.castShadow = shouldVehicleCastShadow(hero, quality);
          }
        });
        return;
      }
      detailGroup.visible = false;
      solarGroup.visible = false;
      authoredHero.setQuality(quality);
      if (shadowProxy !== null) {
        shadowProxy.visible = quality !== "low";
        shadowProxy.castShadow = quality !== "low";
      }
      contactPatch.castShadow = false;
    },
    setDistanceEmphasis(emphasis) {
      root.scale.setScalar(heroScale * THREE.MathUtils.clamp(emphasis, 1, 1.55));
    },
    setVisible(visible) {
      root.visible = visible;
    },
    dispose() {
      suspensionPresentation.dispose();
      authoredHero?.dispose();
      shadowProxyGeometry?.dispose();
      shadowProxyMaterial?.dispose();
      materials.dispose();
      root.removeFromParent();
      root.clear();
    },
  };
};

const HERO_PALETTE: VehiclePalette = {
  paint: 0x234a78,
  accent: 0xe3dccb,
  solar: 0xb74732,
  metal: 0x899295,
};

const RIVAL_PALETTES: readonly VehiclePalette[] = [
  { paint: 0x9f2f2b, accent: 0x1d2226, solar: 0xe4ded0, metal: 0xb8bec0 },
  { paint: 0xe1ddd0, accent: 0x243e5c, solar: 0xb74732, metal: 0x9fa8ab },
  { paint: 0x31553f, accent: 0xd4c08a, solar: 0xe5e0d2, metal: 0xa2a9a6 },
  { paint: 0xc7932e, accent: 0x252b2d, solar: 0xe5e0d2, metal: 0xb5ada0 },
  { paint: 0x2a3034, accent: 0xd9d2c3, solar: 0xb74732, metal: 0x7c8588 },
];

export type SuntraceVehicleFleet = {
  group: THREE.Group;
  hero: SuntraceVehicleVisual;
  rivals: readonly SuntraceVehicleVisual[];
  resetPresentation: () => void;
  setQuality: (quality: SuntraceQuality) => void;
  dispose: () => void;
};

export const createSuntraceVehicleFleet = (
  authoredHero: SuntraceAuthoredVehicleAsset | null = null,
): SuntraceVehicleFleet => {
  const geometries = createVehicleGeometries();
  const group = new THREE.Group();
  group.name = "Asterion prototype field";
  const hero = createVehicleVisual(
    geometries,
    HERO_PALETTE,
    2,
    true,
    authoredHero,
  );
  const rivals = RIVAL_PALETTES.map((palette, index) =>
    createVehicleVisual(geometries, palette, index, false),
  );
  group.add(hero.root, ...rivals.map((rival) => rival.root));

  return {
    group,
    hero,
    rivals,
    resetPresentation() {
      hero.resetPresentation();
      for (const rival of rivals) {
        rival.resetPresentation();
      }
    },
    setQuality(quality) {
      hero.setQuality(quality);
      for (const rival of rivals) {
        rival.setQuality(quality);
      }
    },
    dispose() {
      hero.dispose();
      for (const rival of rivals) {
        rival.dispose();
      }
      geometries.dispose();
      group.removeFromParent();
      group.clear();
    },
  };
};
